import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useBottomNavContext } from '../context/BottomNavContext';
import QuickAdd from './QuickAdd';
import EventsNavButton from './EventsNavButton';
import { resolveUploadUrl } from '../utils/api';
import classes from './BottomNav.module.scss';

const HomeIcon = () => (
  <svg className={classes.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V21h5v-6h4v6h5V9.5" />
  </svg>
);

const ChatIcon = () => (
  <svg className={classes.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5h13a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H9l-5 4V8a3 3 0 0 1 0-3z" />
  </svg>
);

const initial = (name?: string) => (name?.trim()?.[0] || '?').toUpperCase();

const BottomNav = () => {
  const location = useLocation();
  const user = useSelector((s: RootState) => s.user.user);
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const unreadMessages = useSelector((s: RootState) => s.messages.unreadCount);
  const { contextual } = useBottomNavContext();

  // Contracts is a document-heavy, form-heavy page (upload panel, ask
  // textarea, drafts). The floating pill covers content on shorter
  // viewports and adds no value there — nothing on /contracts hangs
  // off the app-level nav. Hide it site-wide (AU + ZA users alike).
  if (location.pathname === '/contracts') return null;

  // /building/:id/ask is the full-screen concierge experience — the pinned
  // composer sits at the bottom already, so an overlapping floating nav
  // both covers input and competes for attention. Hide it there.
  if (/^\/building\/[^/]+\/ask\/?$/.test(location.pathname)) return null;

  // ── Contextual mode ────────────────────────────────────────────────────────
  // A page has registered its own tab set (e.g. BuildingDetail: 5 tabs).
  // Rendered regardless of auth state so public visitors can still see the
  // page structure — pills that need a sign-in are marked `disabled` by the
  // host page and greyed out here.
  if (contextual) {
    return (
      <nav
        className={`${classes.nav} ${classes['nav--contextual']}`}
        aria-label={contextual.ariaLabel || 'Section navigation'}
      >
        <div className={classes.contextRow} role="tablist" aria-label={contextual.ariaLabel || 'Section navigation'}>
          {contextual.items.filter(i => !i.hidden).map((item) => {
            const isActive = item.id === contextual.active;
            const iconOnly = !!item.icon;
            const labelText = typeof item.label === 'string' ? item.label : undefined;
            const disabled = !!item.disabled;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-disabled={disabled || undefined}
                disabled={disabled}
                aria-label={iconOnly ? labelText : undefined}
                title={iconOnly ? labelText : undefined}
                className={`${classes.contextPill} ${iconOnly ? classes['contextPill--icon'] : ''} ${isActive ? classes['contextPill--active'] : ''} ${item.accent ? classes['contextPill--accent'] : ''} ${disabled ? classes['contextPill--disabled'] : ''}`}
                onClick={() => { if (!disabled) contextual.onChange(item.id); }}
              >
                {iconOnly ? (
                  <span className={classes.contextPill__icon} aria-hidden="true">{item.icon}</span>
                ) : (
                  <span>{item.label}</span>
                )}
                {item.count !== undefined && item.count !== null && item.count > 0 && (
                  <span className={classes.contextPill__count}>{item.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    );
  }

  // The app-level nav below is only relevant to signed-in users — the
  // home/profile/messages slots all rely on user state.
  if (!isAuthenticated) return null;

  // ── Default app-level 5-slot nav ───────────────────────────────────────────
  const homeTarget = user.id ? '/my' : '/';
  const homeActive = location.pathname === '/my' || location.pathname === `/${user.id}` || location.pathname === '/';
  const chatActive = location.pathname === '/messages';
  const profileTarget = user.id ? `/${user.id}` : '/login';
  const profileActive = user.id ? location.pathname === `/${user.id}` : false;

  return (
    <nav className={classes.nav} aria-label="Primary">
      <Link
        to={homeTarget}
        className={`${classes.slot} ${homeActive ? classes.active : ''}`}
        aria-label="Home"
        aria-current={homeActive ? 'page' : undefined}
      >
        <HomeIcon />
      </Link>

      <Link
        to={profileTarget}
        className={classes.slot}
        aria-label="Profile"
        aria-current={profileActive ? 'page' : undefined}
      >
        <span className={`${classes.avatarSlot} ${profileActive ? classes.active : ''}`} aria-hidden="true">
          {user.avatar_url ? (
            <img
              src={resolveUploadUrl(user.avatar_url)}
              alt=""
              className={classes.avatarImg}
            />
          ) : (
            initial(user.name)
          )}
        </span>
      </Link>

      <QuickAdd />

      <EventsNavButton />

      <Link
        to="/messages"
        className={`${classes.slot} ${chatActive ? classes.active : ''}`}
        aria-label="Messages"
        aria-current={chatActive ? 'page' : undefined}
      >
        <ChatIcon />
        {unreadMessages > 0 && (
          <span className={classes.dot} aria-label={`${unreadMessages} unread`}>
            {unreadMessages > 99 ? '99+' : unreadMessages}
          </span>
        )}
      </Link>
    </nav>
  );
};

export default BottomNav;
